import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { openFileInNewTab, downloadFileAttachment } from '../../../services/fileApi';
import { signedDocBadgeClass, signedDocStatusLabel } from '../Screening/screeningQueueDisplay';
import { resolveSignedPackRows } from '../../../utils/applicationSignedPdfPacks';

function formatDateTime(value) {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Read-only signed application PDF block for staff submission views.
 * Supports multi-PDF forms (one card per pack with an uploaded signed file).
 */
const SubmissionSignedDocumentPanel = ({
  signedApplicationDocument,
  signedPdfPacks,
  formSettings,
  submission,
  canView = true,
  preferDownload = false,
  className = '',
}) => {
  const [openingKey, setOpeningKey] = useState('');

  const source = submission || {
    signedApplicationDocument,
    signedPdfPacks,
    applicationForm: { settings: formSettings },
    formSettings,
  };
  const rows = resolveSignedPackRows(source).filter((r) => String(r.signed?.fileId || '').trim());
  if (!rows.length) return null;

  const handleView = async (doc, packKey) => {
    const fileId = String(doc.fileId || '').trim();
    if (!fileId) return;
    const fileName = doc.originalName || doc.fileName || 'Signed application PDF';
    setOpeningKey(packKey);
    try {
      if (preferDownload) {
        await downloadFileAttachment(fileId, fileName, 'application/pdf');
      } else {
        await openFileInNewTab(fileId, { mimeType: 'application/pdf' });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || (preferDownload ? 'Could not download signed PDF' : 'Could not open signed PDF'));
    } finally {
      setOpeningKey('');
    }
  };

  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-5 space-y-3 ${className}`.trim()}>
      <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Signed application PDF{rows.length > 1 ? 's' : ''}</p>
      {rows.map((row) => {
        const doc = row.signed || {};
        const status = String(doc.status || 'SUBMITTED').toUpperCase();
        const fileName = doc.originalName || doc.fileName || 'Signed application PDF';
        const uploadedAt = formatDateTime(doc.uploadedAt);
        const verifiedAt = formatDateTime(doc.verifiedAt);
        const opening = openingKey === row.packKey;

        return (
          <div
            key={row.packKey}
            className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50/60 p-3"
          >
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-800 mb-1">{row.title}</p>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className={`inline-flex px-2 py-0.5 rounded-md border text-xs font-medium ${signedDocBadgeClass(status)}`}>
                  {signedDocStatusLabel(status, doc.uploadSource)}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900 truncate" title={fileName}>{fileName}</p>
              {uploadedAt && (
                <p className="text-xs text-gray-500 mt-1">Uploaded {uploadedAt}</p>
              )}
              {status === 'VERIFIED' && verifiedAt && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Verified {verifiedAt}
                  {doc.verifiedByName ? ` by ${doc.verifiedByName}` : ''}
                </p>
              )}
            </div>
            {canView && (
              <button
                type="button"
                onClick={() => handleView(doc, row.packKey)}
                disabled={opening}
                className="inline-flex shrink-0 items-center px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-xs font-medium text-indigo-800 hover:bg-indigo-100 disabled:opacity-50"
              >
                {opening ? (preferDownload ? 'Downloading…' : 'Opening…') : (preferDownload ? 'Download' : 'View')}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SubmissionSignedDocumentPanel;
